import { useEffect, useState } from 'react';

const CHARACTER_DELAY_MS = 32;
const SPACE_DELAY_MS = 18;
const PHRASE_HOLD_MS = 720;
const PHRASE_EXIT_MS = 200;
const EMPTY_PAUSE_MS = 90;
const LAST_PHRASE_PAUSE_MS = 300;
const noop = () => undefined;

export type TTypewriterPhrasePhase = 'typing' | 'holding' | 'exiting' | 'empty' | 'lastPause' | 'completed';

export interface IUseTypewriterPhrasesResult {
  typedText: string;
  announcedPhrase: string;
  phase: TTypewriterPhrasePhase;
}

export const useTypewriterPhrases = (phrasesFunc: () => string[]): IUseTypewriterPhrasesResult => {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [typedLength, setTypedLength] = useState(0);
  const [announcedPhrase, setAnnouncedPhrase] = useState('');
  const [phase, setPhase] = useState<TTypewriterPhrasePhase>('typing');
  const [phrases] = useState(phrasesFunc);

  const phrase = phrases[phraseIndex] ?? '';
  const typedText = phrase.slice(0, typedLength);

  useEffect(() => {
    if (!phrase || phase === 'completed') {
      return noop;
    }

    if (phase === 'typing') {
      if (typedLength < phrase.length) {
        const character = phrase[typedLength];
        const delay = character === ' ' ? SPACE_DELAY_MS : CHARACTER_DELAY_MS;
        const timer = window.setTimeout(() => setTypedLength(length => length + 1), delay);

        return () => window.clearTimeout(timer);
      }

      setAnnouncedPhrase(phrase);
      setPhase(phraseIndex === phrases.length - 1 ? 'lastPause' : 'holding');

      return noop;
    }

    if (phase === 'holding') {
      const timer = window.setTimeout(() => setPhase('exiting'), PHRASE_HOLD_MS);

      return () => window.clearTimeout(timer);
    }

    if (phase === 'exiting') {
      const timer = window.setTimeout(() => {
        setTypedLength(0);
        setPhase('empty');
      }, PHRASE_EXIT_MS);

      return () => window.clearTimeout(timer);
    }

    if (phase === 'empty') {
      const timer = window.setTimeout(() => {
        setPhraseIndex(index => index + 1);
        setPhase('typing');
      }, EMPTY_PAUSE_MS);

      return () => window.clearTimeout(timer);
    }

    if (phase === 'lastPause') {
      const timer = window.setTimeout(() => setPhase('completed'), LAST_PHRASE_PAUSE_MS);

      return () => window.clearTimeout(timer);
    }

    return noop;
  }, [phrase, phraseIndex, phrases.length, phase, typedLength]);

  return { announcedPhrase, phase, typedText };
};
